import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Crown,
  Copy,
  Check,
  Play,
  Share2,
  Wifi,
  WifiOff,
  LogOut,
  Download,
} from 'lucide-react';
import { useGameStore } from '@/store';
import { useWebSocket } from '@/hooks/useWebSocket';
import { logger } from '@/utils/logger';
import { soundManager } from '@/utils/soundManager';

export function LobbyPage() {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const { player, room, players, isHost, gameSettings, reset } = useGameStore();
  const [copied, setCopied] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  
  // Calculate minimum players based on spy count
  const spyCount = gameSettings?.spyCount || 1;
  const minPlayers = spyCount === 2 ? 4 : 3;
  const canStartGame = players.length >= minPlayers;
  
  // Notifications for player join/leave
  const [notifications, setNotifications] = useState<{ id: string; message: string; type: 'join' | 'leave' }[]>([]);

  // Debug log for players
  useEffect(() => {
    logger.info('LobbyPage', 'Players state changed', { 
      playersCount: players.length, 
      players: players.map(p => ({ id: p.id, name: p.name, role: p.role })),
      currentPlayer: player?.id
    });
  }, [players, player]);

  // WebSocket connection
  const { isConnected, sendMessage, on } = useWebSocket(code!, player?.id!, player?.name!);

  // Add notification with auto-remove after 5 seconds
  const addNotification = (message: string, type: 'join' | 'leave') => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Handle WebSocket messages with callback system
  useEffect(() => {
    const unsubJoin = on('room:player-joined', (message) => {
      const joinData = message.payload as { 
        playerId: string; 
        playerName: string; 
      };
      if (joinData.playerId !== player?.id && joinData.playerName) {
        addNotification(`${joinData.playerName} انضم للغرفة`, 'join');
        soundManager.playSuccess();
      }
    });

    const unsubLeave = on('room:player-left', (message) => {
      const leaveData = message.payload as { 
        playerId: string; 
        playerName?: string; 
      };
      if (leaveData.playerName && leaveData.playerId !== player?.id) {
        addNotification(`${leaveData.playerName} غادر الغرفة`, 'leave');
      }
    });

    const unsubClosed = on('room:closed', () => {
      soundManager.playError();
      alert('تم إغلاق الغرفة من قبل المضيف');
      reset();
      navigate('/');
    });

    return () => {
      unsubJoin();
      unsubLeave();
      unsubClosed();
    };
  }, [on, player?.id, reset, navigate]);

  // Redirect if no player info
  useEffect(() => {
    if (!player || !room) {
      navigate(`/join/${code}`);
    }
  }, [player, room, code, navigate]);

  // Copy room code
  const copyCode = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Share room
  const shareRoom = async () => {
    if (!code) return;
    const url = `${window.location.origin}/join/${code}`;
    
    if (navigator.share) {
      await navigator.share({
        title: 'انضم للعبة الحفلة!',
        text: `انضم للعبة معي! كود الغرفة: ${code}`,
        url,
      });
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Start game
  const handleStartGame = () => {
    logger.info('LobbyPage', 'Start game button clicked', { isHost, playersCount: players.length, minPlayers });
    if (!isHost || !canStartGame) {
      logger.warn('LobbyPage', 'Cannot start game', { isHost, playersCount: players.length, minPlayers });
      return;
    }
    setIsStarting(true);
    logger.info('LobbyPage', 'Sending game:start message');
    sendMessage({ type: 'game:start', payload: {} });
  };

  // Leave room
  const handleLeave = () => {
    if (isHost) {
      // Host closing room - notify server
      sendMessage({ type: 'room:close', payload: {} });
    } else {
      // Player leaving - notify server
      sendMessage({ type: 'room:leave', payload: {} });
    }
    reset();
    navigate('/');
  };

  // Use useMemo for sorted players to react to changes
  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);

  return (
    <div className="min-h-screen p-4">
      {/* ==================== NOTIFICATIONS ==================== */}
      <div className="fixed top-4 left-4 right-4 z-[100] flex flex-col items-center gap-2 pointer-events-none">
        <AnimatePresence>
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.9 }}
              transition={{ type: 'spring', damping: 20 }}
              className={`px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium pointer-events-auto ${
                notification.type === 'join'
                  ? 'bg-emerald-500/90 text-white'
                  : 'bg-orange-500/90 text-white'
              }`}
            >
              <span className="text-lg">👋</span>
              <span>{notification.message}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={handleLeave}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            مغادرة
          </button>

          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="flex items-center gap-1 text-game-success text-sm">
                <Wifi className="w-4 h-4" />
                متصل
              </span>
            ) : (
              <span className="flex items-center gap-1 text-game-accent text-sm">
                <WifiOff className="w-4 h-4" />
                غير متصل
              </span>
            )}
          </div>
        </motion.div>

        {/* Room Info Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-white/60 text-sm mb-1">كود الغرفة</p>
              <div className="flex items-center gap-3">
                <h1 
                  className="text-4xl font-bold tracking-widest font-mono"
                  style={{ direction: 'ltr' }}
                >
                  {code}
                </h1>
                <button
                  onClick={copyCode}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-game-success" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="text-left">
              <span className={`badge ${room?.gameType === 'spy' ? 'badge-accent' : 'badge-gold'}`}>
                {room?.gameType === 'spy' ? '🕵️ الجاسوس' : '❓ أسئلة'}
              </span>
            </div>
          </div>

          <button
            onClick={shareRoom}
            className="btn-outline w-full flex items-center justify-center gap-2"
          >
            <Share2 className="w-5 h-5" />
            شارك الرابط مع أصدقائك
          </button>
        </motion.div>

        {/* Players List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-400" />
              اللاعبون
            </h2>
            <span className="text-white/60">
              {players.length} / {room?.maxPlayers || 10}
            </span>
          </div>

          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {sortedPlayers.map((p, index) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-3 p-3 rounded-xl ${
                    p.id === player?.id
                      ? 'bg-primary-500/20 border border-primary-500/30'
                      : 'bg-white/5'
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-game-accent to-pink-500 flex items-center justify-center text-xl font-bold">
                    {p.name.charAt(0)}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{p.name}</span>
                      {p.role === 'host' && (
                        <Crown className="w-4 h-4 text-game-gold" />
                      )}
                      {p.id === player?.id && (
                        <span className="text-xs text-white/40">(أنت)</span>
                      )}
                    </div>
                    <span className={`text-sm ${p.isConnected ? 'text-game-success' : 'text-white/40'}`}>
                      {p.isConnected ? 'متصل' : 'غير متصل'}
                    </span>
                  </div>

                  {/* Score */}
                  <div className="text-left">
                    <span className="text-game-gold font-bold">{p.score}</span>
                    <span className="text-white/40 text-sm"> نقطة</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Empty slots */}
            {Array.from({ length: Math.max(0, 2 - players.length) }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-white/10"
              >
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white/20" />
                </div>
                <span className="text-white/30">في انتظار لاعب...</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Start Game Button (Host only) */}
        {isHost && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {/* Minimum players warning */}
            {!canStartGame && (
              <div className={`mb-4 p-3 rounded-xl text-center ${
                spyCount === 2 
                  ? 'bg-amber-500/20 border border-amber-500/30 text-amber-300'
                  : 'bg-blue-500/20 border border-blue-500/30 text-blue-300'
              }`}>
                <p className="flex items-center justify-center gap-2">
                  <span>{spyCount === 2 ? '⚠️' : 'ℹ️'}</span>
                  {spyCount === 2 
                    ? `تحتاج ${minPlayers - players.length} لاعب إضافي على الأقل (الحد الأدنى ${minPlayers} لاعبين للعب بجاسوسين)`
                    : `تحتاج ${minPlayers - players.length} لاعب إضافي على الأقل (الحد الأدنى ${minPlayers} لاعبين)`
                  }
                </p>
                {spyCount === 2 && (
                  <p className="text-xs mt-1 text-amber-200/70">🗳️ كل لاعب سيحصل على صوتين في التصويت</p>
                )}
              </div>
            )}
            
            <button
              onClick={handleStartGame}
              disabled={!canStartGame || isStarting || !isConnected}
              className="btn-success w-full flex items-center justify-center gap-3 text-xl py-4"
            >
              {isStarting ? (
                <>
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري بدء اللعبة...
                </>
              ) : (
                <>
                  <Play className="w-6 h-6" />
                  ابدأ اللعبة
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Waiting message (Non-host) */}
        {!isHost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center text-white/60"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-2 h-2 bg-game-gold rounded-full animate-pulse" />
              <div className="w-2 h-2 bg-game-gold rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
              <div className="w-2 h-2 bg-game-gold rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
            في انتظار بدء اللعبة من المضيف...
          </motion.div>
        )}

        {/* Debug Button - Download Logs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <button
            onClick={() => logger.downloadLogs()}
            className="btn-outline text-sm flex items-center gap-2 mx-auto"
          >
            <Download className="w-4 h-4" />
            تحميل سجل الأخطاء
          </button>
        </motion.div>
      </div>
    </div>
  );
}
